import { Avatar, Box, Grid, IconButton } from '@mui/material';
import { GridColumns } from '@mui/x-data-grid';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { LuTrash2 } from 'react-icons/lu';
import { TbCirclePlus } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';
import { ITeam } from 'wisestep-api';
import {
  WsButton,
  WsColors,
  WsDataGrid,
  WsDrawer,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import Spinner from '../../../../components/Spinner';
import { IOption } from '../../../../components/TeamsOptions';
import { useWorkspaceContext } from '../../../../context';
import ComingSoonSvg from '../../../../images/coming_soon.svg';
import { success, failed } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import { NavigationSidebar } from '../../../automatedEmailCampaignSettings';
import WsAvatar from '../../../teams/components/WsAvatar';
import WsLink from '../../../teams/components/WsLink';

import {
  getIcons,
  getNavigationLink,
  navigateToOrgUnit,
  removeDuplicates,
  userAccessActionApiMap,
  userAccessNavOptions,
} from './helper/userAccessUtil';
import { UserAccessTypes } from './types/userProfileTypes';
import UserAccessProvider from './UserAccessProvider';

const UserAccessViewer = ({ email }: { email: string }) => {
  const workspaceContext = useWorkspaceContext();
  const [loading, setLoading] = useState<boolean>(false);
  const [pagination, setPagination] = useState({ page: 0, pageSize: 50 });
  const [activeItem, setActiveItem] = useState<UserAccessTypes>('Teams');
  const [entities, setEntities] = useState<ITeam[]>([]);
  const [drawer, setDrawer] = useState<boolean>(false);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const navigate = useNavigate();

  useEffect(() => {
    const controller = new AbortController();

    const fetchEntities = async () => {
      setLoading(true);
      try {
        const response = await userAccessActionApiMap['VIEW'][activeItem]?.(email, {
          signal: controller.signal,
        });
        response?.data ? setEntities(response.data) : setEntities([]);
      } catch (e) {
        setEntities([]);
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchEntities();

    return () => controller.abort();
  }, [email, activeItem]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 0, pageSize: 50 }));
  }, [activeItem]);

  const handleDelete = useCallback(
    async (id: string) => {
      try {
        await userAccessActionApiMap['DELETE'][activeItem]?.(id, email);
        setNotification(success(`${activeItem} removed successfully`));
        setEntities(entities.filter((team) => team.id !== id));
      } catch {
        setNotification(failed(`Failed to remove from ${activeItem.toLowerCase()}`));
      }
    },
    [activeItem, email, setNotification, entities]
  );

  const handleClickItem = (item: IOption) => setActiveItem(item.label as UserAccessTypes);

  const openDrawer = () => setDrawer(true);
  const closeDrawer = () => setDrawer(false);

  const handleSaveAction = (selectedEntities: ITeam[]) => {
    closeDrawer();
    setEntities((prev) => removeDuplicates([...prev, ...selectedEntities]));
  };

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const handlePageSizeChange = (pageSize: number) => {
    setPagination((prev) => ({ ...prev, pageSize }));
  };

  const columns: GridColumns = useMemo(
    () => [
      {
        field: 'name',
        headerName: activeItem,
        flex: 1,
        renderCell: (params) => {
          const value =
            activeItem === 'Client Contacts'
              ? `${params.row.firstName ?? ''} ${params.row.lastName ?? ''}`
              : params.row.name;

          const handleNavigate = () => navigateToOrgUnit(params.row, navigate);

          return (
            <Grid spacing={2} alignItems="center" container>
              <Grid item>
                <WsAvatar {...getIcons(activeItem)} />
              </Grid>
              <Grid item>
                {[
                  'Client Units',
                  'Account Manager Units',
                  'Client Contact Units',
                  'Delivery Units',
                ].includes(activeItem) ? (
                  <WsTypography
                    cursor="pointer"
                    color={WsTypoColors.PRIMARY750}
                    onClick={handleNavigate}
                  >
                    {value}
                  </WsTypography>
                ) : (
                  <WsLink to={getNavigationLink(params.row, activeItem)}>
                    <WsTypography>{value}</WsTypography>
                  </WsLink>
                )}
              </Grid>
            </Grid>
          );
        },
      },
      ...(activeItem === 'Client Contacts'
        ? [
            {
              field: 'clientName',
              headerName: 'Client Name',
              flex: 1,
              renderCell: (params) => (
                <WsLink
                  to={`/client-details?name=${params.row.clientName}&isChild=true&clientId=${params.row.clientId}&tabIndex=0`}
                >
                  <WsTypography>{params.value}</WsTypography>
                </WsLink>
              ),
            },
          ]
        : []),
      ...(workspaceContext.owns
        ? [
            {
              field: 'action',
              headerName: '',
              width: 90,
              renderCell: (params) => {
                const handleDeleteMember = () => handleDelete(params.row.id);
                return (
                  <IconButton aria-label="delete" onClick={handleDeleteMember}>
                    <LuTrash2 size={20} />
                  </IconButton>
                );
              },
            },
          ]
        : []),
    ],
    [activeItem, handleDelete, navigate, workspaceContext.owns]
  );

  return (
    <Grid container columnGap={2}>
      <Grid bgcolor={WsColors.White} item xs={2} borderRadius={2}>
        <NavigationSidebar sideMenuOptions={userAccessNavOptions} onClick={handleClickItem} />
      </Grid>
      <Grid item xs bgcolor={WsColors.White} borderRadius={2} p={2} position="relative">
        {[
          'Job Board Accounts',
          'Data Access',
          'SMS Number',
          'Call Number',
          'Business Units',
        ].includes(activeItem) ? (
          <Box textAlign="center" height={700} width="100%">
            <WsTypography variant={WsTypoVariants.TITLE} color={WsTypoColors.PRIMARY750}>
              Coming Soon
            </WsTypography>
            <Avatar
              variant="square"
              sx={{
                width: '100%',
                height: '100%',
                mb: 0,
                '& img': {
                  objectFit: 'contain',
                },
              }}
              src={ComingSoonSvg}
            />
          </Box>
        ) : (
          <>
            <Box display="flex" justifyContent="space-between" py={1}>
              <WsTypography variant={WsTypoVariants.TITLE}>{activeItem}</WsTypography>
              {workspaceContext.owns && (
                <WsButton startIcon={<TbCirclePlus size={20} />} onClick={openDrawer}>
                  {`Add ${activeItem}`}
                </WsButton>
              )}
            </Box>
            {loading && <Spinner posY={15} backdrop />}
            <WsDataGrid
              autoHeight
              rows={entities}
              columns={columns}
              page={pagination.page}
              onPageChange={handlePageChange}
              pageSize={pagination.pageSize}
              onPageSizeChange={handlePageSizeChange}
            />
          </>
        )}
      </Grid>
      <WsDrawer title={`Add ${activeItem}`} size="small" open={drawer} onClose={closeDrawer}>
        <UserAccessProvider email={email} activeItem={activeItem} onSave={handleSaveAction} />
      </WsDrawer>
    </Grid>
  );
};

export default UserAccessViewer;
